import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { WebXRControllerComponent } from '@babylonjs/core/XR/motionController/webXRControllerComponent';

const dead = (value: number) =>
  !Number.isFinite(value) || Math.abs(value) < 0.15 ? 0 : Math.max(-1, Math.min(1, value));
export const TABLE_MOVE_BOUNDS = { radius: 3, centerZ: -0.7, minY: 0.2, maxY: 4 } as const;
export interface TableSticks {
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
  reset: boolean;
}

/** WebXR runtimes disagree about whether handedness lives on the source or motion profile. */
export function controllerHandedness(controller: {
  inputSource?: { handedness?: XRHandedness };
  motionController?: { handedness?: string } | null;
}): string {
  const source = controller.inputSource?.handedness;
  return source === 'left' || source === 'right'
    ? source
    : controller.motionController?.handedness || 'none';
}

/** Positive pitch/yaw mean toward the current screen's up/right, not world Euler angles. */
export function tableMotion(sticks: TableSticks, elapsed: number) {
  const dt = Number.isFinite(elapsed) ? Math.min(0.05, Math.max(0, elapsed)) : 0;
  const x = dead(sticks.leftX),
    y = dead(sticks.leftY);
  const magnitude = Math.max(1, Math.hypot(x, y));
  return {
    forward: (-y / magnitude) * 0.9 * dt,
    side: (x / magnitude) * 1.1 * dt,
    yaw: dead(sticks.rightX) * 1.3 * dt,
    pitch: -dead(sticks.rightY) * 0.9 * dt,
  };
}

/**
 * How the right stick turns the view. By default in steps about the head, level with the floor: a
 * smooth artificial turn, and a tilt above all, is what makes a seated player queasy (x-quest-vr#3).
 * `free` is the mapping of 18 September 2026 (docs/temple-and-quest.md), a setting now: smooth
 * turns toward any side of the view, through full loops.
 */
export interface TableTurning {
  free: boolean;
  /** How far one flick turns, in degrees, when not free. */
  snapDegrees: number;
}

/** A flick past this turns one step; the stick must come back under `SNAP_REARM` for another. */
const SNAP_PUSH = 0.7;
const SNAP_REARM = 0.3;

/**
 * The step a flick of the right stick asks for: 1 to the right, −1 to the left or 0. It fires once
 * as the stick passes `SNAP_PUSH`, and again only once the stick has come back near the middle,
 * so a stick held over turns one step, not one a frame.
 */
export function snapStep(x: number, armed: boolean): { step: -1 | 0 | 1; armed: boolean } {
  const v = Number.isFinite(x) ? x : 0;
  if (armed && Math.abs(v) >= SNAP_PUSH) return { step: v > 0 ? 1 : -1, armed: false };
  return { step: 0, armed: armed || Math.abs(v) < SNAP_REARM };
}

/** Clamp artificial travel at the eyes, not the rig origin. Never snap real tracking back in. */
export function boundedTablePosition(from: Vector3, desired: Vector3): Vector3 {
  const b = TABLE_MOVE_BOUNDS;
  const next = desired.clone();
  next.y = Math.max(Math.min(b.minY, from.y), Math.min(Math.max(b.maxY, from.y), next.y));
  const limit = Math.max(b.radius, Math.hypot(from.x, from.z - b.centerZ));
  const reach = Math.hypot(next.x, next.z - b.centerZ);
  if (reach > limit) {
    next.x *= limit / reach;
    next.z = b.centerZ + ((next.z - b.centerZ) * limit) / reach;
  }
  return next;
}

export function createXrTableInput(
  xr: WebXRDefaultExperience,
  rig: TransformNode,
  seatHeight: () => number,
  turning: () => TableTurning,
) {
  let resetHeld = false;
  let snapArmed = true;
  const camera = xr.baseExperience.camera;
  rig.rotationQuaternion = Quaternion.Identity();
  /** Which way the head looks, about the vertical; none while it looks straight up or down. */
  const heading = (): number | null => {
    const forward = camera.getForwardRay().direction;
    return Math.hypot(forward.x, forward.z) > 1e-3 ? Math.atan2(forward.x, forward.z) : null;
  };
  /**
   * Where the head sat as the session began, and which way it looked: the seat. Babylon starts the
   * headset where the screen's camera stood, facing the same way, and the rig is level there.
   */
  let seat: { x: number; z: number; heading: number } | null = null;
  /** The rig level and at the seat's height, with no turn or travel: only the tracking moves the head. */
  const level = () => {
    rig.rotationQuaternion!.copyFrom(Quaternion.Identity());
    rig.position.set(0, seatHeight(), 0);
    rig.computeWorldMatrix(true);
    camera.computeWorldMatrix();
  };
  const takeSeat = () => {
    camera.computeWorldMatrix();
    const at = camera.globalPosition;
    seat = { x: at.x, z: at.z, heading: heading() ?? 0 };
  };
  // The rig is levelled as Babylon sets the headset's first pose, and the seat is taken on the
  // session's first frame, from the head the tracking gives then: the pose Babylon asks for is not
  // always the one it gets (the IWER emulator leaves the head at the room's origin).
  let seatDue = false;
  xr.baseExperience.onInitialXRPoseSetObservable.add(() => {
    level();
    seat = null;
    seatDue = true;
  });
  // After the camera's own update, which Babylon puts first.
  xr.baseExperience.sessionManager.onXRFrameObservable.add(() => {
    if (!seatDue) return;
    seatDue = false;
    takeSeat();
  });
  /**
   * Level again, and turned about the vertical and slid so that the head is back on the seat,
   * looking the way it did: wherever the player has moved to or turned their chair since. It only
   * zeroed the rig, so a player who sat down to one side, or turned their chair, saw nothing move
   * (x-quest-vr#12).
   */
  const reset = () => {
    level();
    if (!seat) return takeSeat();
    // Looking straight up or down there is no heading to turn from: the turn is left as it is.
    const now = heading();
    if (now !== null) {
      const off = seat.heading - now;
      // The shorter way round; a head already facing the seat's way is not turned by float noise.
      const turn = Math.atan2(Math.sin(off), Math.cos(off));
      if (Math.abs(turn) > 1e-6) {
        rig.rotationQuaternion = Quaternion.RotationAxis(Vector3.Up(), turn);
        rig.computeWorldMatrix(true);
        camera.computeWorldMatrix();
      }
    }
    // Within a tenth of a millimetre the head is on the seat already.
    const head = camera.globalPosition;
    const dx = seat.x - head.x;
    const dz = seat.z - head.z;
    if (Math.abs(dx) > 1e-4) rig.position.x += dx;
    if (Math.abs(dz) > 1e-4) rig.position.z += dz;
    rig.computeWorldMatrix(true);
  };
  const pivot = (rotation: Quaternion) => {
    // Turn around the eyes, not the room origin: a seated head must not orbit the table.
    camera.computeWorldMatrix();
    const head = camera.globalPosition.clone();
    const offset = rig.position.subtract(head);
    offset.rotateByQuaternionToRef(rotation, offset);
    rig.position.copyFrom(head.add(offset));
    rig.rotationQuaternion = rotation.multiply(rig.rotationQuaternion!).normalize();
    rig.computeWorldMatrix(true);
  };
  return {
    reset,
    level,
    update(dt: number, enabled: boolean) {
      if (!enabled) {
        resetHeld = false;
        snapArmed = true;
        return;
      }
      const input: TableSticks = { leftX: 0, leftY: 0, rightX: 0, rightY: 0, reset: false };
      for (const controller of xr.input.controllers) {
        const motion = controller.motionController;
        if (!motion) continue;
        const stick = motion.getComponentOfType(WebXRControllerComponent.THUMBSTICK_TYPE);
        input.reset ||= !!(motion.getComponent('a-button') ?? motion.getComponent('x-button'))
          ?.pressed;
        input.reset ||= !!stick?.pressed;
        if (!stick) continue;
        const handedness = controllerHandedness(controller);
        if (handedness === 'left') {
          input.leftX = stick.axes.x;
          input.leftY = stick.axes.y;
        }
        if (handedness === 'right') {
          input.rightX = stick.axes.x;
          input.rightY = stick.axes.y;
        }
      }
      if (input.reset && !resetHeld) reset();
      resetHeld = input.reset;
      if (input.reset) return;
      const move = tableMotion(input, dt);
      camera.computeWorldMatrix();
      const turn = turning();
      if (!turn.free) {
        // A step about the head, round the floor's own vertical: nothing tilts, nothing glides.
        const flick = snapStep(input.rightX, snapArmed);
        snapArmed = flick.armed;
        if (flick.step)
          pivot(
            Quaternion.RotationAxis(Vector3.Up(), (flick.step * turn.snapDegrees * Math.PI) / 180),
          );
      } else if (move.yaw || move.pitch) {
        const forward = camera.getForwardRay().direction.normalize();
        const up = camera.getDirection(Vector3.Up()).normalize();
        const right = camera.getDirection(Vector3.Right()).normalize();
        // Cross products specify the desired change in the rendered gaze directly.
        // This works facing either +/-Z, after physical head turns, and upside down.
        const axis = Vector3.Cross(forward, up)
          .scale(move.pitch)
          .addInPlace(Vector3.Cross(forward, right).scale(move.yaw));
        const angle = axis.length();
        if (angle > 0) pivot(Quaternion.RotationAxis(axis.scale(1 / angle), angle));
      }
      if (move.forward || move.side) {
        camera.computeWorldMatrix();
        const head = camera.globalPosition.clone();
        const delta = camera
          .getForwardRay()
          .direction.normalize()
          .scale(move.forward)
          .addInPlace(camera.getDirection(Vector3.Right()).normalize().scale(move.side));
        rig.position.addInPlace(boundedTablePosition(head, head.add(delta)).subtract(head));
        rig.computeWorldMatrix(true);
      }
    },
  };
}
